import { useState } from 'react';
import { useProfileSettings } from './ProfileSettingsContext';
import ProfileEditorForm from './ProfileEditorForm';
import ArchitectSection from './ArchitectSection';

export default function AutofillTab() {
  const {
    profiles, selectedProfileId, setSelectedProfileId,
    handleSaveProfile, savingAutofill,
    editingName, setEditingName,
    handleCreateProfile, handleDeleteProfile, handleSetDefault
  } = useProfileSettings();

  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [copyFromId, setCopyFromId] = useState('');

  const selectedProfile = profiles.find(p => p.id === selectedProfileId);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
        <h3 className="text-xl font-bold mb-2">Autofill Profiles</h3>
        <p className="text-sm opacity-90 max-w-2xl">
          Create specialized profiles for different job roles. Use the Resume Architect to auto-populate your details from existing resume files.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Profile List */}
        <div className="lg:col-span-1 space-y-4">
           <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-4">
                 <div>
                    <h4 className="font-bold text-gray-900 dark:text-white uppercase text-[10px] tracking-widest mb-1">Reference Library</h4>
                    <p className="text-[10px] text-gray-400 font-medium leading-tight">Your collection of master profiles used by the extension for AI tailoring.</p>
                 </div>
                 <button 
                  onClick={() => setShowNewModal(true)}
                  className="p-1.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300 rounded-lg hover:bg-purple-200"
                 >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M12 4v16m8-8H4"/></svg>
                 </button>
              </div>

              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                 {profiles.map(p => (
                    <div 
                      key={p.id}
                      onClick={() => {
                        setSelectedProfileId(p.id);
                        setEditingName(p.name);
                      }}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedProfileId === p.id 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                       <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-gray-900 dark:text-white truncate">{p.name}</span>
                          {p.isDefault && <span className="text-[9px] font-bold uppercase tracking-widest text-purple-500 bg-purple-100 dark:bg-purple-900 px-1.5 py-0.5 rounded">Default</span>}
                       </div>
                    </div>
                 ))}
              </div>
           </div>
        </div>

        {/* Right: Editor Area */}
        <div className="lg:col-span-2 space-y-6">
           {selectedProfile ? (
              <>
                 {/* Header Actions */}
                 <div className="flex flex-wrap items-center justify-between gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <input 
                      type="text"
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      className="flex-1 bg-transparent text-lg font-bold text-gray-900 dark:text-white outline-none"
                      placeholder="Profile Name"
                    />
                    <div className="flex items-center gap-2">
                       {!selectedProfile.isDefault && (
                         <button onClick={() => handleSetDefault(selectedProfile.id)} className="text-xs font-bold text-purple-600 hover:underline">Set Default</button>
                       )}
                       <button onClick={handleDeleteProfile} className="text-xs font-bold text-red-500 hover:underline">Delete</button>
                    </div>
                 </div>

                 {/* Resume Architect Section */}
                 <ArchitectSection />

                 {/* Main Form Fields */}
                 <ProfileEditorForm />

                 {/* Sticky Save Bar */}
                 <div className="sticky bottom-6 flex justify-end">
                    <button 
                      onClick={handleSaveProfile}
                      disabled={savingAutofill}
                      className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-full shadow-xl hover:bg-indigo-700 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
                    >
                       {savingAutofill ? 'Saving Changes...' : 'Save Profile'}
                    </button>
                 </div>
              </>
           ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700">
                 <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                 </div>
                 <h3 className="text-lg font-bold text-gray-900 dark:text-white">Select a Profile</h3>
                 <p className="text-sm text-gray-500 max-w-xs mt-2">Pick a profile from the library to edit, or create a new one to start tailoring.</p>
              </div>
           )}
        </div>
      </div>

      {/* New Profile Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
           <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-6 shadow-2xl">
              <h3 className="text-xl font-bold mb-4">Create New Profile</h3>
              <div className="space-y-4">
                 <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Name</label>
                    <input 
                      type="text" 
                      value={newName} 
                      onChange={e => setNewName(e.target.value)} 
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl"
                      placeholder="Software Engineer..."
                    />
                 </div>
                 <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Copy From (Optional)</label>
                    <select 
                      value={copyFromId} 
                      onChange={e => setCopyFromId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl"
                    >
                       <option value="">None (Start Fresh)</option>
                       {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                 </div>
                 <div className="flex gap-3 pt-4">
                    <button onClick={() => setShowNewModal(false)} className="flex-1 px-4 py-2 font-bold text-gray-500 hover:bg-gray-100 rounded-xl">Cancel</button>
                    <button 
                      onClick={() => {
                        handleCreateProfile(newName, copyFromId);
                        setShowNewModal(false);
                      }} 
                      className="flex-1 px-4 py-2 font-bold bg-purple-600 text-white rounded-xl hover:bg-purple-700"
                    >
                       Create
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}
    </div>
  );
}
